import { apiClient, type Paginated } from '@/core/api';
import type {
  LibraryFolderDto,
  LibraryFolderListParams,
  LibraryFolderWriteInput,
  LibraryItemDto,
  LibraryItemListParams,
  LibraryItemUpdateInput,
  LibraryUploadInput,
  LibraryUsageDto,
} from './types';

/** The folder tree is small by construction (three levels, ADR-018) — read it
 *  whole and build the tree client-side rather than walking level by level. */
export const FOLDER_PAGE_SIZE = 200;

// ---------------------------------------------------------------------------
// Folders
// ---------------------------------------------------------------------------

/** `GET /api/library-folders/` — every school member reads the whole tree
 *  (it carries no content). */
export async function getLibraryFolders(
  params: LibraryFolderListParams = {}
): Promise<Paginated<LibraryFolderDto>> {
  const { data } = await apiClient.get<Paginated<LibraryFolderDto>>('/library-folders/', {
    params: { page_size: FOLDER_PAGE_SIZE, ...params },
  });
  return data;
}

/** `POST /api/library-folders/` — staff; answers in the read shape. */
export async function createLibraryFolder(
  input: LibraryFolderWriteInput
): Promise<LibraryFolderDto> {
  const { data } = await apiClient.post<LibraryFolderDto>('/library-folders/', input);
  return data;
}

/** `PATCH /api/library-folders/{public_id}/` — rename or re-parent. Depth and
 *  cycle rules are the server's; surface `field_errors.parent` verbatim. */
export async function updateLibraryFolder(
  publicId: string,
  input: LibraryFolderWriteInput
): Promise<LibraryFolderDto> {
  const { data } = await apiClient.patch<LibraryFolderDto>(`/library-folders/${publicId}/`, input);
  return data;
}

/** `DELETE /api/library-folders/{public_id}/` — a HARD delete that cascades to
 *  sub-folders; items inside survive with `folder = null`. */
export async function deleteLibraryFolder(publicId: string): Promise<void> {
  await apiClient.delete(`/library-folders/${publicId}/`);
}

// ---------------------------------------------------------------------------
// Items
// ---------------------------------------------------------------------------

/** `GET /api/library/` — staff see the whole shelf; pupils see school-wide +
 *  their classes; parents see school-wide + their children's classes. */
export async function getLibraryItems(
  params: LibraryItemListParams = {}
): Promise<Paginated<LibraryItemDto>> {
  const { data } = await apiClient.get<Paginated<LibraryItemDto>>('/library/', { params });
  return data;
}

/** `GET /api/library/{public_id}/` — 404 when the item is out of scope. */
export async function getLibraryItem(publicId: string): Promise<LibraryItemDto> {
  const { data } = await apiClient.get<LibraryItemDto>(`/library/${publicId}/`);
  return data;
}

/**
 * `POST /api/library/` — staff multipart upload. `kind`, `size`,
 * `content_type` and `original_filename` are derived server-side from the file;
 * `tags` travels as a JSON-encoded array (DRF's JSONField parses HTML/multipart
 * input as JSON).
 */
export async function uploadLibraryItem(
  input: LibraryUploadInput,
  onProgress?: (percent: number) => void
): Promise<LibraryItemDto> {
  const form = new FormData();
  form.append('file', input.file);
  form.append('title', input.title);
  if (input.author) form.append('author', input.author);
  if (input.tags) form.append('tags', JSON.stringify(input.tags));
  if (input.folder) form.append('folder', input.folder);
  if (input.school_class) form.append('school_class', input.school_class);
  const { data } = await apiClient.post<LibraryItemDto>('/library/', form, {
    onUploadProgress: event => {
      if (onProgress && event.total) {
        onProgress(Math.round((event.loaded / event.total) * 100));
      }
    },
  });
  return data;
}

/** `PATCH /api/library/{public_id}/` — metadata only; the file is immutable. */
export async function updateLibraryItem(
  publicId: string,
  input: LibraryItemUpdateInput
): Promise<LibraryItemDto> {
  const { data } = await apiClient.patch<LibraryItemDto>(`/library/${publicId}/`, input);
  return data;
}

/** `DELETE /api/library/{public_id}/` — archives (C4); the file stays. */
export async function deleteLibraryItem(publicId: string): Promise<void> {
  await apiClient.delete(`/library/${publicId}/`);
}

/** `GET /api/library/usage/` — staff only; `{items, bytes}`, reported not
 *  enforced. */
export async function getLibraryUsage(): Promise<LibraryUsageDto> {
  const { data } = await apiClient.get<LibraryUsageDto>('/library/usage/');
  return data;
}
